import React, { useEffect, useMemo, useState } from "react";
import {
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  SlidersHorizontal,
  Check,
  X,
} from "lucide-react";
import ProductCard from "../../components/product/ProductCard";
import Dropdown from "../ui/Dropdown";

// Move these OUTSIDE the component to avoid re-creating them on every render
const GENDERS = ["All", "Men", "Women"];
const CATEGORIES = [
  "All",
  "Rings",
  "Set",
  "Necklaces",
  "Earrings",
  "Bracelets",
  "Chains",
  "Pendants",
  "Cufflinks",
  "Brooches",
];
const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "name", label: "Name: A to Z" },
];
const AVAILABILITY = ["All", "In stock", "Sold out"];
// In the default "All" view only the most recently sold-out pieces are shown,
// enough to signal demand without filling the page. "Sold out" lists them all.
const SOLD_OUT_PREVIEW = 2;

const PRICE_LABELS = {
  "0-50": "Under $50",
  "50-100": "$50 - $100",
  "100-50000": "$100 +",
};

// Collapsible filter group: only the title shows until it is clicked. While
// closed, the active choice (if any) is shown as a pill next to the title.
const FilterSection = ({ title, value, open, onToggle, children }) => (
  <div className="border-b border-gray-100 last:border-b-0">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex items-center justify-between w-full gap-2 py-4 text-left"
    >
      <span className="font-medium text-gray-900">{title}</span>
      <span className="flex items-center gap-2 min-w-0">
        {value && !open && (
          <span className="truncate px-2 py-0.5 text-xs font-medium rounded-full text-[#1a1a3e] bg-[#e8e4f3]">
            {value}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 text-gray-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </span>
    </button>
    {open && <div className="pb-4 space-y-2">{children}</div>}
  </div>
);

// What the grid will actually show for a set with `inStock` + `soldOut` items.
const visibleCount = ({ inStock, soldOut }, availability) =>
  availability === "In stock"
    ? inStock
    : availability === "Sold out"
    ? soldOut
    : inStock + Math.min(soldOut, SOLD_OUT_PREVIEW);

const METALS = [
  "All",
  "Yellow Gold",
  "White Gold",
  "Platinum",
  "Silver",
  "Rose Gold",
  "Stainless Steel",
  "Titanium",
  "Tungsten",
];

const ShopPage = ({ products = [], onViewProduct, state, onStateChange }) => {
  const {
    selectedGender,
    selectedCategory,
    priceRange,
    selectedMetal,
    availability = "All",
    sortBy,
    currentPage,
    itemsPerPage,
  } = state;

  const updateState = (updater) =>
    onStateChange((prev) =>
      typeof updater === "function" ? updater(prev) : { ...prev, ...updater }
    );

  const setSelectedGender = (value) =>
    updateState((prev) => ({ ...prev, selectedGender: value, currentPage: 1 }));
  const setSelectedCategory = (value) =>
    updateState((prev) => ({ ...prev, selectedCategory: value, currentPage: 1 }));
  const setPriceRange = (value) =>
    updateState((prev) => ({ ...prev, priceRange: value, currentPage: 1 }));
  const setSelectedMetal = (value) =>
    updateState((prev) => ({ ...prev, selectedMetal: value, currentPage: 1 }));
  const setAvailability = (value) =>
    updateState((prev) => ({ ...prev, availability: value, currentPage: 1 }));
  const setSortBy = (value) =>
    updateState((prev) => ({ ...prev, sortBy: value, currentPage: 1 }));
  const setItemsPerPage = (value) =>
    updateState((prev) => ({ ...prev, itemsPerPage: value, currentPage: 1 }));
  const setCurrentPage = (value) =>
    updateState((prev) => ({
      ...prev,
      currentPage:
        typeof value === "function" ? value(prev.currentPage) : value,
    }));

  // Mobile filter sheet. Filters apply live, so closing it is all "apply" does.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const section = (key) => ({
    open: Boolean(openGroups[key]),
    onToggle: () => setOpenGroups((g) => ({ ...g, [key]: !g[key] })),
  });
  const sheetOpen = filtersOpen || sortOpen;

  useEffect(() => {
    if (!sheetOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [sheetOpen]);

  // Calculate available counts for each filter option. Each option tallies
  // in-stock and sold-out separately so its count matches what the grid shows
  // under the current availability setting.
  const filterCounts = useMemo(() => {
    const tally = { gender: {}, category: {}, metal: {}, availability: {} };
    const bump = (group, key, product) => {
      const t = (tally[group][key] = tally[group][key] || {
        inStock: 0,
        soldOut: 0,
      });
      t[product.outOfStock ? "soldOut" : "inStock"]++;
    };

    products.forEach((product) => {
      const genderMatch =
        selectedGender === "All" || product.gender === selectedGender;
      const categoryMatch =
        selectedCategory === "All" || product.subcategory === selectedCategory;
      const priceMatch =
        product.price >= priceRange[0] && product.price <= priceRange[1];
      const metalMatch =
        selectedMetal === "All" || product.metal === selectedMetal;

      // Each group's counts consider every other active filter
      if (categoryMatch && priceMatch && metalMatch) {
        bump("gender", product.gender, product);
        bump("gender", "All", product);
      }
      if (genderMatch && priceMatch && metalMatch) {
        bump("category", product.subcategory, product);
        bump("category", "All", product);
      }
      if (genderMatch && categoryMatch && priceMatch) {
        bump("metal", product.metal, product);
        bump("metal", "All", product);
      }
      if (genderMatch && categoryMatch && priceMatch && metalMatch) {
        bump("availability", "All", product);
      }
    });

    const counts = { gender: {}, category: {}, metal: {}, availability: {} };
    ["gender", "category", "metal"].forEach((group) =>
      Object.entries(tally[group]).forEach(([key, t]) => {
        counts[group][key] = visibleCount(t, availability);
      })
    );
    const all = tally.availability.All || { inStock: 0, soldOut: 0 };
    AVAILABILITY.forEach((option) => {
      counts.availability[option] = visibleCount(all, option);
    });
    return counts;
  }, [
    products,
    selectedGender,
    selectedCategory,
    selectedMetal,
    priceRange,
    availability,
  ]);

  // Filter products
  let filteredProducts = products.filter((product) => {
    const genderMatch =
      selectedGender === "All" || product.gender === selectedGender;
    const categoryMatch =
      selectedCategory === "All" || product.subcategory === selectedCategory;
    const priceMatch =
      product.price >= priceRange[0] && product.price <= priceRange[1];
    const metalMatch =
      selectedMetal === "All" || product.metal === selectedMetal;
    const availabilityMatch =
      availability === "All" ||
      (availability === "Sold out") === Boolean(product.outOfStock);
    return (
      genderMatch && categoryMatch && priceMatch && metalMatch && availabilityMatch
    );
  });

  // Default view: keep only the most recently sold-out pieces.
  if (availability === "All") {
    const preview = filteredProducts
      .filter((p) => p.outOfStock)
      .sort((a, b) => (b.updated || "").localeCompare(a.updated || ""))
      .slice(0, SOLD_OUT_PREVIEW);
    filteredProducts = filteredProducts.filter(
      (p) => !p.outOfStock || preview.includes(p)
    );
  }

  // Sort products. Sold-out pieces stay listed for a while but always go last.
  if (sortBy === "price-low") {
    filteredProducts = [...filteredProducts].sort((a, b) => a.price - b.price);
  } else if (sortBy === "price-high") {
    filteredProducts = [...filteredProducts].sort((a, b) => b.price - a.price);
  } else if (sortBy === "name") {
    filteredProducts = [...filteredProducts].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }
  filteredProducts = [
    ...filteredProducts.filter((p) => !p.outOfStock),
    ...filteredProducts.filter((p) => p.outOfStock),
  ];

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const clearFilters = () => {
    updateState((prev) => ({
      ...prev,
      selectedGender: "All",
      selectedCategory: "All",
      selectedMetal: "All",
      availability: "All",
      priceRange: [0, 5000],
      sortBy: "featured",
      currentPage: 1,
    }));
  };

  const activeFilterCount = [
    selectedGender !== "All",
    selectedCategory !== "All",
    selectedMetal !== "All",
    availability !== "All",
    !(priceRange[0] === 0 && priceRange[1] === 5000),
  ].filter(Boolean).length;

  // Pagination helper
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  const filterGroups = (
    <>
      {/* Gender Filter */}
      <FilterSection
        title="Shop For"
        value={selectedGender !== "All" && selectedGender}
        {...section("gender")}
      >
        {GENDERS.map((gender) => {
          const count = filterCounts.gender[gender] || 0;
          const isDisabled = count === 0 && gender !== "All";

          return (
            <button
              key={gender}
              onClick={() => !isDisabled && setSelectedGender(gender)}
              disabled={isDisabled}
              className={`w-full text-left px-4 py-2 rounded-lg transition relative ${
                selectedGender === gender
                  ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
                  : isDisabled
                  ? "text-gray-400 cursor-not-allowed bg-gray-50"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{gender}</span>
                {isDisabled && (
                  <span className="text-xs italic text-gray-400">
                    Coming Soon
                  </span>
                )}
                {!isDisabled && gender !== "All" && (
                  <span className="text-xs text-gray-500">
                    ({count})
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </FilterSection>

      {/* Category Filter */}
      <FilterSection
        title="Category"
        value={selectedCategory !== "All" && selectedCategory}
        {...section("category")}
      >
        {CATEGORIES.map((cat) => {
          const count = filterCounts.category[cat] || 0;
          const isDisabled = count === 0 && cat !== "All";

          return (
            <button
              key={cat}
              onClick={() => !isDisabled && setSelectedCategory(cat)}
              disabled={isDisabled}
              className={`w-full text-left px-4 py-2 rounded-lg transition ${
                selectedCategory === cat
                  ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
                  : isDisabled
                  ? "text-gray-400 cursor-not-allowed bg-gray-50"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{cat}</span>
                {isDisabled && (
                  <span className="text-xs italic text-gray-400">
                    Coming Soon
                  </span>
                )}
                {!isDisabled && cat !== "All" && (
                  <span className="text-xs text-gray-500">
                    ({count})
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </FilterSection>

      {/* Metal Type Filter */}
      <FilterSection
        title="Metal Type"
        value={selectedMetal !== "All" && selectedMetal}
        {...section("metal")}
      >
        {METALS.map((metal) => {
          const count = filterCounts.metal[metal] || 0;
          const isDisabled = count === 0 && metal !== "All";

          return (
            <button
              key={metal}
              onClick={() => !isDisabled && setSelectedMetal(metal)}
              disabled={isDisabled}
              className={`w-full text-left px-4 py-2 rounded-lg transition ${
                selectedMetal === metal
                  ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
                  : isDisabled
                  ? "text-gray-400 cursor-not-allowed bg-gray-50"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{metal}</span>
                {isDisabled && (
                  <span className="text-xs italic text-gray-400">
                    Coming Soon
                  </span>
                )}
                {!isDisabled && metal !== "All" && (
                  <span className="text-xs text-gray-500">
                    ({count})
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </FilterSection>

      {/* Availability Filter */}
      <FilterSection
        title="Availability"
        value={availability !== "All" && availability}
        {...section("availability")}
      >
        {AVAILABILITY.map((option) => {
          const count = filterCounts.availability[option] || 0;
          const isDisabled = count === 0 && option !== "All";

          return (
            <button
              key={option}
              onClick={() => !isDisabled && setAvailability(option)}
              disabled={isDisabled}
              className={`w-full text-left px-4 py-2 rounded-lg transition ${
                availability === option
                  ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
                  : isDisabled
                  ? "text-gray-400 cursor-not-allowed bg-gray-50"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{option}</span>
                {option !== "All" && (
                  <span className="text-xs text-gray-500">({count})</span>
                )}
              </div>
            </button>
          );
        })}
      </FilterSection>

      {/* Price Range Filter */}
      <FilterSection
        title="Price Range"
        value={PRICE_LABELS[priceRange.join("-")]}
        {...section("price")}
      >
        <button
          onClick={() => setPriceRange([0, 50])}
          className={`w-full text-left px-4 py-2 rounded-lg transition ${
            priceRange[0] === 0 && priceRange[1] === 50
              ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          Under $50
        </button>
        <button
          onClick={() => setPriceRange([50, 100])}
          className={`w-full text-left px-4 py-2 rounded-lg transition ${
            priceRange[0] === 50 && priceRange[1] === 100
              ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          $50 - $100
        </button>
        <button
          onClick={() => setPriceRange([100, 50000])}
          className={`w-full text-left px-4 py-2 rounded-lg transition ${
            priceRange[0] === 100 && priceRange[1] === 50000
              ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          $100 +
        </button>
        <button
          onClick={() => setPriceRange([0, 5000])}
          className={`w-full text-left px-4 py-2 rounded-lg transition ${
            priceRange[0] === 0 && priceRange[1] === 5000
              ? "bg-[#e8e4f3] text-[#1a1a3e] font-medium"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          All Prices
        </button>
      </FilterSection>
    </>
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 sm:py-12 lg:px-8">
      <div className="mb-4 lg:mb-8">
        <h1 className="mb-2 font-serif text-3xl text-gray-900 sm:text-4xl">
          Collections
        </h1>
        <p className="text-gray-600">
          Discover our complete collection of fine jewelry
        </p>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row">
        {/* Filters Sidebar (desktop) */}
        <div className="flex-shrink-0 hidden lg:block lg:w-64">
          <div className="sticky p-6 bg-white border border-gray-100 shadow-sm rounded-2xl top-24">
            <div className="flex items-center justify-between mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <Filter className="w-5 h-5" />
                Filters
              </h3>
              <button
                onClick={clearFilters}
                className="text-sm text-[#d4af37] hover:text-[#c49a2e]"
              >
                Clear All
              </button>
            </div>

            {filterGroups}
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1">
          {/* Quick gender chips (mobile) */}
          <div className="flex gap-2 pb-1 mb-3 -mx-4 overflow-x-auto px-4 lg:hidden">
            {GENDERS.map((gender) => {
              const count = filterCounts.gender[gender] || 0;
              const isDisabled = count === 0 && gender !== "All";
              return (
                <button
                  key={gender}
                  onClick={() => !isDisabled && setSelectedGender(gender)}
                  disabled={isDisabled}
                  className={`flex-shrink-0 px-5 py-2 rounded-full text-sm border transition ${
                    selectedGender === gender
                      ? "bg-[#1a1a3e] border-[#1a1a3e] text-white font-medium"
                      : isDisabled
                      ? "border-gray-200 text-gray-400"
                      : "bg-white border-gray-300 text-gray-700"
                  }`}
                >
                  {gender}
                </button>
              );
            })}
          </div>

          {/* Sticky Filters / Sort bar (mobile) */}
          <div className="sticky z-30 flex items-center gap-3 px-4 py-3 mb-4 -mx-4 border-b border-gray-100 top-20 bg-white/95 backdrop-blur lg:hidden">
            <button
              onClick={() => setFiltersOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-900 border border-gray-300 rounded-full"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center w-5 h-5 text-xs text-white rounded-full bg-[#d4af37]">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <span className="text-sm text-gray-500">
              {filteredProducts.length} items
            </span>
            <button
              onClick={() => setSortOpen(true)}
              className="flex items-center gap-1 py-2 pl-4 pr-3 ml-auto text-sm font-medium text-gray-900 border border-gray-300 rounded-full"
            >
              Sort
              {sortBy !== "featured" && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
              )}
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Top Bar (desktop) */}
          <div className="items-center justify-between hidden gap-4 p-4 mb-6 bg-white border border-gray-100 shadow-sm lg:flex rounded-xl">
            <div className="flex items-center gap-4">
              <p className="text-gray-600">
                <span className="font-semibold text-gray-900">
                  {filteredProducts.length}
                </span>{" "}
                products found
              </p>
              {filteredProducts.length > 0 && (
                <span className="text-sm text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <Dropdown
                  value={itemsPerPage}
                  options={[6, 9, 12, 24].map((n) => ({
                    value: n,
                    label: String(n),
                  }))}
                  onChange={setItemsPerPage}
                  ariaLabel="Products per page"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <Dropdown
                  value={sortBy}
                  options={SORT_OPTIONS}
                  onChange={setSortBy}
                  ariaLabel="Sort by"
                  align="right"
                />
              </div>
            </div>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="py-16 text-center bg-white border border-gray-100 shadow-sm rounded-2xl">
              <div className="max-w-md mx-auto">
                <div className="mb-4 text-6xl">💎</div>
                <h3 className="mb-2 font-serif text-2xl text-gray-900">
                  Coming Soon
                </h3>
                <p className="mb-6 text-gray-600">
                  We're currently curating an exquisite collection for this
                  category. Check back soon for new arrivals.
                </p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-[#d4af37] text-white rounded-lg hover:bg-[#c49a2e] transition"
                >
                  View All Products
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Products Grid */}
              <div className="grid grid-cols-2 gap-3 mb-8 sm:gap-6 lg:grid-cols-3">
                {currentProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onViewDetails={onViewProduct}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col items-center justify-between gap-4 p-4 bg-white border border-gray-100 shadow-sm sm:flex-row rounded-xl">
                  {/* Page Info */}
                  <p className="text-sm text-gray-600">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, filteredProducts.length)} of{" "}
                    {filteredProducts.length} products
                  </p>

                  {/* Pagination Controls */}
                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => {
                        setCurrentPage((prev) => Math.max(1, prev - 1));
                        scrollToTop();
                      }}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg border transition ${
                        currentPage === 1
                          ? "border-gray-200 text-gray-400 cursor-not-allowed"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, index) => (
                        <React.Fragment key={index}>
                          {page === "..." ? (
                            <span className="px-3 py-2 text-gray-400">...</span>
                          ) : (
                            <button
                              onClick={() => {
                                setCurrentPage(page);
                                scrollToTop();
                              }}
                              className={`min-w-[40px] px-3 py-2 rounded-lg transition ${
                                currentPage === page
                                  ? "bg-[#d4af37] text-white font-medium"
                                  : "text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              {page}
                            </button>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => {
                        setCurrentPage((prev) =>
                          Math.min(totalPages, prev + 1)
                        );
                        scrollToTop();
                      }}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg border transition ${
                        currentPage === totalPages
                          ? "border-gray-200 text-gray-400 cursor-not-allowed"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Sort sheet (mobile) */}
      {sortOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSortOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 pb-4 bg-white rounded-t-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold">Sort by</h3>
              <button
                onClick={() => setSortOpen(false)}
                aria-label="Close sort"
                className="p-2 -mr-2 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setSortBy(option.value);
                  setSortOpen(false);
                  scrollToTop();
                }}
                className={`flex items-center justify-between w-full px-6 py-4 text-left ${
                  sortBy === option.value
                    ? "text-[#1a1a3e] font-semibold"
                    : "text-gray-600"
                }`}
              >
                {option.label}
                {sortBy === option.value && (
                  <Check className="w-5 h-5 text-[#d4af37]" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filter sheet (mobile) */}
      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setFiltersOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 flex flex-col bg-white rounded-t-2xl max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <Filter className="w-5 h-5" />
                Filters
              </h3>
              <button
                onClick={() => setFiltersOpen(false)}
                aria-label="Close filters"
                className="p-2 -mr-2 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 px-6 pt-6 overflow-y-auto">
              {filterGroups}
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={clearFilters}
                className="flex-1 py-3 text-sm font-medium text-gray-700 border border-gray-300 rounded-full"
              >
                Clear All
              </button>
              <button
                onClick={() => {
                  setFiltersOpen(false);
                  scrollToTop();
                }}
                className="flex-[2] py-3 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-[#1a1a3e] to-[#2a2a5e]"
              >
                Show {filteredProducts.length} products
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPage;
